import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { Pagination } from '@/common/types';
import { API_URL, IFundsHistoryItemDto } from '@/dtos';

import { FundsHistoryTransaction } from '../UnappliedFunds.types';

export const FUNDS_HISTORY_QUERY_KEY = 'FUNDS_HISTORY_QUERY';

interface Props {
  organizationId?: string;
  search?: string;
  page: number;
  size: number;
}

const fetchFundsHistory = ({ organizationId, search, page, size }: Props) =>
  api.get<Pagination<IFundsHistoryItemDto>>(API_URL.UNMATCHED_PUSH_TRANSACTIONS_FUNDS_HISTORY_SEARCH, {
    params: {
      organization_id: Number(organizationId),
      search: search || undefined,
      page,
      size,
    },
  });

const formatTransaction = (item: IFundsHistoryItemDto): FundsHistoryTransaction => ({
  id: String(item.id),
  amount: item.amount ?? 0,
  date: item.activity_date ? dayjs(item.activity_date).format(DEFAULT_DATE_FORMAT) : '-',
  description: item.description ?? '-',
});

const useFundsHistoryQuery = ({ organizationId, search, page, size }: Props) => {
  const { data, isLoading } = useQuery(
    [FUNDS_HISTORY_QUERY_KEY, organizationId, search, page, size],
    () => fetchFundsHistory({ organizationId, search, page, size }),
    { enabled: Boolean(organizationId) }
  );

  const transactions = useMemo(
    () => (data?.data?.content ?? []).map(formatTransaction),
    [data?.data?.content]
  );

  return {
    transactions,
    total: data?.data?.totalElements ?? 0,
    isLoading,
  };
};

export default useFundsHistoryQuery;
